// @migration-in-progress
/**
 * Familiarisation PDF Export Functions
 * Generates PDF reports for vessel familiarisation records
 */

import jsPDF from 'jspdf';
import { addReportHeader, addReportFooter, formatDate } from './pdfCore';

/**
 * Convert N/A reason code to human-readable text
 */
const getNAReasonText = (reasonCode) => {
  const reasons = {
    'not_required_rank': 'Not required by rank/role',
    'equipment_not_onboard': 'Equipment not onboard',
    'not_applicable_vessel': 'Not applicable to this vessel type',
    'system_not_installed': 'System not installed',
    'electric_propulsion': 'Electric propulsion vessel',
    'no_passenger_ops': 'No passenger operations',
    'no_cargo_ops': 'No cargo/lifting operations',
    'open_deck_only': 'Open deck vessel only',
    'not_required_category': 'Not required for vessel category'
  };

  // Handle "other: custom reason" format
  if (reasonCode?.startsWith('other:')) {
    return reasonCode.substring(7).trim();
  }

  return reasons[reasonCode] || reasonCode || 'Not specified';
};

/**
 * Internal helper - generates the PDF document
 * @returns {{ doc: jsPDF, filename: string }}
 */
const generateFamiliarisationPDF = (record, crewMember, vessel) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);

  // Header
  let yPos = addReportHeader(doc, 20, {
    title: 'Vessel Familiarisation Record',
    vesselName: vessel?.name || record.vesselName || 'Unknown Vessel',
    category: vessel?.category || record.category || 'Unknown'
  });

  // Title - use template name if available (Phase 3)
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  const certificateTitle = record.checklistName
    ? `${record.checklistName} Certificate`
    : 'Vessel Familiarisation Certificate';
  doc.text(certificateTitle, pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;

  // Vessel Info Box
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, yPos, contentWidth, 25, 'F');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Vessel: ${vessel?.name || record.vesselName}`, margin + 5, yPos + 8);
  doc.text(`Type: ${vessel?.type || record.vesselType}`, margin + 5, yPos + 16);
  doc.text(`Category: ${vessel?.category || record.category}`, pageWidth / 2, yPos + 8);
  doc.text(`Date: ${formatDate(record.completedAt)}`, pageWidth / 2, yPos + 16);
  yPos += 35;

  // Crew Member Info
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Crew Member Details', margin, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 8;

  doc.setFontSize(10);
  doc.text(`Name: ${crewMember?.full_name || record.crewName}`, margin, yPos);
  yPos += 6;
  doc.text(`Position: ${record.signOffData?.crewPosition || record.crewPosition || crewMember?.position || 'Not specified'}`, margin, yPos);
  yPos += 15;

  // Completed Sections
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Completed Familiarisation Sections', margin, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 12;  // More space before table

  // Table of completed sections
  const checklists = record.vesselChecklists || {};

  // Table header
  doc.setFillColor(66, 165, 245);
  doc.rect(margin, yPos, contentWidth, 10, 'F');  // Taller header (10px instead of 8px)
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.text('Section', margin + 2, yPos + 7);      // Text centered vertically in 10px bar
  doc.text('Items Completed', margin + 80, yPos + 7);
  doc.text('Status', margin + 130, yPos + 7);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
  yPos += 14;  // More space after header (10px bar + 4px gap)

  // Table rows
  Object.entries(checklists).forEach(([section, data]) => {
    const items = data.items || [];
    const totalItems = items.length;

    // Count completed items for this section by checking each item's ID
    let completedCount = 0;
    items.forEach((item, index) => {
      const itemId = item.id || `${section}-item-${index}`;
      const itemStatus = record.completedChecklists?.[itemId];
      if (itemStatus && (itemStatus.status === 'completed' || itemStatus.status === 'na')) {
        completedCount++;
      }
    });

    doc.setFontSize(9);
    doc.text(data.title || data.name || section, margin + 2, yPos);
    doc.text(`${completedCount}/${totalItems}`, margin + 80, yPos);
    doc.text(completedCount === totalItems ? 'Completed' : 'Partial', margin + 130, yPos);
    doc.line(margin, yPos + 3, margin + contentWidth, yPos + 3);  // Line slightly lower
    yPos += 8;  // More space between rows (8px instead of 6px)
  });

  // Handle case where no checklists exist
  if (Object.keys(checklists).length === 0) {
    doc.setFontSize(9);
    doc.text('No section data available - record created before this feature', margin + 2, yPos);
    yPos += 6;
  }

  yPos += 10;

  // Detailed Item Listing (for MCA audit trail)
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Detailed Familiarisation Record', margin, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 12;  // More space before items

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('Individual items verified during familiarisation process', margin, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 8;

  // List each section with its items
  Object.entries(checklists).forEach(([sectionKey, sectionData]) => {
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Section header
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos - 4, contentWidth, 8, 'F');
    doc.text(sectionData.title || sectionKey, margin + 2, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 10;

    // Items in this section
    const items = sectionData.items || [];
    items.forEach((item, index) => {
      // Check for page break
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }

      const itemId = item.id || `${sectionKey}-item-${index}`;
      const itemStatus = record.completedChecklists?.[itemId];

      // Draw checkbox
      const checkboxX = margin + 2;
      const checkboxY = yPos - 3;
      const checkboxSize = 3.5;

      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.3);
      doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);

      // Fill based on status
      if (itemStatus) {
        if (itemStatus.status === 'completed') {
          // Green filled checkbox with checkmark
          doc.setFillColor(34, 139, 34);
          doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize, 'F');
          // Draw checkmark
          doc.setDrawColor(255, 255, 255);
          doc.setLineWidth(0.5);
          doc.line(checkboxX + 0.7, checkboxY + 1.8, checkboxX + 1.4, checkboxY + 2.7);
          doc.line(checkboxX + 1.4, checkboxY + 2.7, checkboxX + 2.8, checkboxY + 0.8);
        } else if (itemStatus.status === 'na') {
          // Amber/orange filled with N/A text - more visible than gray
          doc.setFillColor(245, 158, 11);  // Amber-500
          doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize, 'F');
          doc.setFontSize(5);
          doc.setTextColor(255, 255, 255);  // White text on amber
          doc.text('N/A', checkboxX + 0.3, checkboxY + 2.5);
        }
      }
      // Reset
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);

      // Item text (shifted right to account for checkbox)
      const itemText = item.text || item.name || itemId;
      const truncatedText = itemText.length > 55 ? itemText.substring(0, 52) + '...' : itemText;
      doc.text(truncatedText, margin + 8, yPos);

      // WBC3 reference (positioned to avoid timestamp)
      if (item.reference) {
        doc.setTextColor(100, 100, 100);
        const refText = item.reference.length > 20 ? item.reference.substring(0, 17) + '...' : item.reference;
        doc.text(refText, pageWidth - margin - 60, yPos);  // Moved left from -45 to -60
        doc.setTextColor(0, 0, 0);
      }

      // Timestamp (far right)
      if (itemStatus?.timestamp) {
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        const timeStr = new Date(itemStatus.timestamp).toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
        });
        doc.text(timeStr, pageWidth - margin - 2, yPos, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
      }

      yPos += 5;

      // Display N/A reason if item was marked as N/A
      if (itemStatus?.status === 'na' && itemStatus?.reason) {
        doc.setFontSize(7);
        doc.setTextColor(180, 120, 0);  // Amber-brown to match N/A box
        // Convert reason code to readable text
        const reasonText = getNAReasonText(itemStatus.reason);
        doc.text(`- Reason: ${reasonText}`, margin + 10, yPos);  // Simple dash instead of arrow
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        yPos += 4;
      }
    });

    yPos += 5; // Space between sections
  });

  yPos += 5;

  // Topic Notes Section (if any notes exist)
  const topicNotes = record.topicNotes || {};
  const hasNotes = Object.values(topicNotes).some(note => note && note.trim());

  if (hasNotes) {
    // Check if we need a new page
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Additional Notes', margin, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 10;

    doc.setFontSize(9);
    Object.entries(topicNotes).forEach(([sectionKey, notes]) => {
      if (notes && notes.trim()) {
        // Check for page break
        if (yPos > 260) {
          doc.addPage();
          yPos = 20;
        }

        // Section name
        const sectionTitle = checklists[sectionKey]?.title || sectionKey;
        doc.setFont(undefined, 'bold');
        doc.text(sectionTitle + ':', margin, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 5;

        // Notes text (wrapped)
        doc.setTextColor(60, 60, 60);
        const noteLines = doc.splitTextToSize(notes.trim(), contentWidth - 10);
        doc.text(noteLines, margin + 5, yPos);
        yPos += noteLines.length * 4 + 5;
        doc.setTextColor(0, 0, 0);
      }
    });

    yPos += 5;
  }

  // Compliance Statement
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const complianceText = 'This familiarisation has been completed in accordance with UK Workboat Code Edition 3, ' +
    'Appendix 8, Sections 7.1 and 7.2. All mandatory sections have been completed, including the minimum ' +
    'requirements of Section 7.2 and additional task-specific training as required by Section 7.1.';

  const lines = doc.splitTextToSize(complianceText, contentWidth);
  doc.text(lines, margin, yPos);
  yPos += lines.length * 5 + 10;

  // Ensure signatures section has enough space (needs ~60px)
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  // Signatures section
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Certification', margin, yPos);
  yPos += 15;

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');

  // Get signature data (check both signOffData and root level for backward compatibility)
  const crewSig = record.signOffData?.crewSignature || record.crewSignature || '';
  const assessorSig = record.signOffData?.assessorSignature || record.assessorSignature || '';
  const crewPos = record.signOffData?.crewPosition || record.crewPosition || '';
  const assessorPos = record.signOffData?.assessorPosition || record.assessorPosition || '';
  const completionDate = record.signOffData?.completionDate || record.completionDate || record.completedAt;

  // Calculate column positions for better alignment
  const col1Start = margin;
  const col2Start = pageWidth / 2 + 10;
  const labelWidth = 35;
  const lineStart = col1Start + labelWidth;
  const lineEnd = col1Start + 85;
  const line2Start = col2Start + labelWidth - 10;
  const line2End = col2Start + 75;

  // Crew Member signature
  doc.text('Crew Member:', col1Start, yPos);
  if (crewSig) {
    if (typeof crewSig === 'object' && crewSig.data) {
      // Render signature image
      try {
        doc.addImage(crewSig.data, 'PNG', lineStart, yPos - 10, 45, 12);
        yPos += 2;
        doc.setFontSize(8);
        doc.text(crewSig.name || '', lineStart + 2, yPos);
        if (crewSig.position) {
          yPos += 4;
          doc.text(crewSig.position, lineStart + 2, yPos);
        }
        doc.setFontSize(10);
      } catch (err) {
        console.warn('Failed to render crew signature image:', err);
        doc.line(lineStart, yPos, lineEnd, yPos);
        doc.setFont(undefined, 'italic');
        doc.text(crewSig.name || crewSig, lineStart + 2, yPos - 2);
        doc.setFont(undefined, 'normal');
      }
    } else {
      // Legacy text signature
      doc.line(lineStart, yPos, lineEnd, yPos);
      doc.setFont(undefined, 'italic');
      doc.text(crewSig, lineStart + 2, yPos - 2);
      doc.setFont(undefined, 'normal');
      yPos += 12;
      doc.text('Position:', col1Start, yPos);
      doc.text(crewPos || '_______________', lineStart + 2, yPos);
    }
  } else {
    doc.line(lineStart, yPos, lineEnd, yPos);
  }

  // Reset yPos for assessor (align with crew signature baseline)
  const assessorYPos = yPos - (typeof crewSig === 'object' && crewSig.data ? (crewSig.position ? 16 : 12) : 0);

  // Assessor signature
  doc.text('Assessor:', col2Start, assessorYPos);
  if (assessorSig) {
    if (typeof assessorSig === 'object' && assessorSig.data) {
      // Render signature image
      try {
        doc.addImage(assessorSig.data, 'PNG', line2Start, assessorYPos - 10, 45, 12);
        let tempYPos = assessorYPos + 2;
        doc.setFontSize(8);
        doc.text(assessorSig.name || '', line2Start + 2, tempYPos);
        if (assessorSig.position) {
          tempYPos += 4;
          doc.text(assessorSig.position, line2Start + 2, tempYPos);
        }
        doc.setFontSize(10);
      } catch (err) {
        console.warn('Failed to render assessor signature image:', err);
        doc.line(line2Start, assessorYPos, line2End, assessorYPos);
        doc.setFont(undefined, 'italic');
        doc.text(assessorSig.name || assessorSig, line2Start + 2, assessorYPos - 2);
        doc.setFont(undefined, 'normal');
      }
    } else {
      // Legacy text signature
      doc.line(line2Start, assessorYPos, line2End, assessorYPos);
      doc.setFont(undefined, 'italic');
      doc.text(assessorSig, line2Start + 2, assessorYPos - 2);
      doc.setFont(undefined, 'normal');
      yPos += 12;
      doc.text('Position:', col2Start, yPos);
      doc.text(assessorPos || '_______________', line2Start + 2, yPos);
    }
  } else {
    doc.line(line2Start, assessorYPos, line2End, assessorYPos);
  }

  // Move yPos past both signatures
  yPos += 12;
  yPos += 12;

  // Date row
  doc.text('Date:', col1Start, yPos);
  doc.text(formatDate(completionDate), lineStart + 2, yPos);

  // Footer
  addReportFooter(doc, {});

  // Generate filename
  const filename = `Familiarisation_${crewMember?.full_name || record.crewName}_${formatDate(record.completedAt)}.pdf`.replace(/\s+/g, '_');

  return { doc, filename };
};

/**
 * Export (download) individual familiarisation record as PDF
 */
export const exportIndividualFamiliarisationPDF = (record, crewMember, vessel) => {
  const { doc, filename } = generateFamiliarisationPDF(record, crewMember, vessel);
  doc.save(filename);
};

/**
 * Preview familiarisation PDF - returns blob URL for display in iframe
 * @returns {{ url: string, filename: string }}
 */
export const previewFamiliarisationPDF = (record, crewMember, vessel) => {
  const { doc, filename } = generateFamiliarisationPDF(record, crewMember, vessel);
  const pdfBlob = doc.output('blob');
  return {
    url: URL.createObjectURL(pdfBlob),
    filename
  };
};

/**
 * Export all familiarisation records summary as PDF
 */
export const exportFamiliarisationSummaryPDF = (records, crewMembers, vessel) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  
  // Header
  let yPos = addReportHeader(doc, 20, {
    title: 'Familiarisation Records Summary',
    vesselName: vessel?.name || 'All Vessels',
    category: vessel?.category || ''
  });
  
  // Title
  doc.setFontSize(16);
  doc.text('Vessel Familiarisation Summary', pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;
  
  // Vessel info
  doc.setFontSize(10);
  doc.text(`Vessel: ${vessel?.name || 'All Vessels'}`, margin, yPos);
  doc.text(`Generated: ${formatDate(new Date())}`, pageWidth - margin - 60, yPos);
  yPos += 15;
  
  // Summary table header
  doc.setFillColor(66, 165, 245);
  doc.rect(margin, yPos, contentWidth, 8, 'F');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.text('Crew Member', margin + 2, yPos + 5);
  doc.text('Position', margin + 50, yPos + 5);
  doc.text('Completed', margin + 90, yPos + 5);
  doc.text('Expires', margin + 120, yPos + 5);
  doc.text('Status', margin + 150, yPos + 5);
  doc.setFont(undefined, 'normal');
  yPos += 10;
  
  // Summary table rows
  records.forEach(record => {
    const crew = crewMembers.find(c => c.id === record.crewId);
    const completedDate = new Date(record.completedAt);
    const expiryDate = new Date(completedDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const daysRemaining = Math.floor((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
    
    let status = 'Valid';
    let statusColor = { r: 76, g: 175, b: 80 }; // Green
    if (daysRemaining < 0) {
      status = 'Expired';
      statusColor = { r: 255, g: 0, b: 0 }; // Red
    } else if (daysRemaining < 30) {
      status = 'Due Soon';
      statusColor = { r: 255, g: 152, b: 0 }; // Orange
    }
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text(crew?.full_name || record.crewName, margin + 2, yPos);
    doc.text(crew?.position || record.signOffData?.crewPosition || 'Unknown', margin + 50, yPos);
    doc.text(formatDate(completedDate), margin + 90, yPos);
    doc.text(formatDate(expiryDate), margin + 120, yPos);
    
    // Color-coded status
    doc.setTextColor(statusColor.r, statusColor.g, statusColor.b);
    doc.text(status, margin + 150, yPos);
    doc.setTextColor(0, 0, 0);
    
    doc.line(margin, yPos + 2, margin + contentWidth, yPos + 2);
    yPos += 6;
  });
  
  // Footer
  addReportFooter(doc, {});

  // Save
  doc.save(`Familiarisation_Summary_${formatDate(new Date())}.pdf`);
};